/**
 * Checklists inside rich text, as plain "☐" and "☑" at the start of a bullet.
 *
 * Shared by meeting notes and quick notes. Deliberately not a widget the
 * editor has to understand: a box that is just a character survives
 * everything the text goes through -- the sanitiser, a company's timeline, a
 * follow-up email, being pasted into Word -- and reads as a checklist in all
 * of them. The list under each editor is where boxes are ticked and turned
 * into tasks.
 */

export const UNCHECKED = "☐";
export const CHECKED = "☑";

export interface ChecklistItem {
  index: number;
  text: string;
  done: boolean;
  /** The first company @mentioned on the line, if any -- what a task hangs off. */
  company: { id: number; name: string } | null;
}

/** Bullets that start with a box, in document order. */
export function checklistOf(html: string): ChecklistItem[] {
  if (!html.includes(UNCHECKED) && !html.includes(CHECKED)) return [];
  const doc = new DOMParser().parseFromString(html, "text/html");
  const items: ChecklistItem[] = [];
  doc.querySelectorAll("li").forEach((li) => {
    const text = (li.textContent ?? "").replace(/ /g, " ").trim();
    if (!text.startsWith(UNCHECKED) && !text.startsWith(CHECKED)) return;
    const mention = li.querySelector<HTMLElement>("[data-company]");
    const id = Number(mention?.dataset.company);
    items.push({
      index: items.length,
      done: text.startsWith(CHECKED),
      text: text.slice(1).trim(),
      company:
        mention && Number.isInteger(id) && id > 0
          ? { id, name: (mention.textContent ?? "").replace(/^@/, "").trim() }
          : null,
    });
  });
  return items;
}

/** Flip the nth checklist box, leaving everything else exactly as written. */
export function toggleItem(html: string, index: number): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  let seen = -1;
  for (const li of Array.from(doc.querySelectorAll("li"))) {
    const walker = doc.createTreeWalker(li, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode() as Text | null;
    while (node && !node.data.trim()) node = walker.nextNode() as Text | null;
    if (!node) continue;
    const at = node.data.search(/\S/);
    const box = node.data[at];
    if (box !== UNCHECKED && box !== CHECKED) continue;
    seen += 1;
    if (seen !== index) continue;
    node.data = node.data.slice(0, at) + (box === UNCHECKED ? CHECKED : UNCHECKED) + node.data.slice(at + 1);
    break;
  }
  return doc.body.innerHTML;
}

/** What the toolbar's Checklist button inserts at the caret. */
export function checklistInsert(): string {
  const inList = window.getSelection()?.anchorNode?.parentElement?.closest("li");
  return inList ? `${UNCHECKED}&nbsp;` : `<ul><li>${UNCHECKED}&nbsp;</li></ul>`;
}
